const POLICY_URL = 'https://pagespy.vmos.cn/api/vmos/reporting/policy'
const SDK_URL = 'https://pagespy.vmos.cn/page-spy/index.min.js'
const ROOM_SESSION_KEY = 'page-spy-room'
const OFF_INTERVAL_MS = 30 * 1000
const DEFAULT_LEASE_SECONDS = 30
const POLICY_TIMEOUT_MS = 10 * 1000

const SENSITIVE_KEY = /(?:token|password|passwd|authorization|cookie|secret|credential|api[-_]?key)/i
const SENSITIVE_QUERY = /([?&](?:token|password|passwd|authorization|cookie|secret|api[-_]?key)=)[^&#]*/gi

export function resolvePageSpyEnvironment(flags = {}) {
  if (flags.ENV_TEST || flags.ENV_DEV) return 'test'
  return 'prod'
}

function redactString(value) {
  return value
    .replace(SENSITIVE_QUERY, '$1[REDACTED]')
    .replace(/((?:Bearer|Basic)\s+)[^\s]+/gi, '$1[REDACTED]')
    .replace(/((?:token|password|passwd|authorization|cookie|secret|credential|api[-_]?key)\s*[=:]\s*["']?)(?!Bearer\b|Basic\b|\[)[^\s,"'}]+/gi, '$1[REDACTED]')
    .replace(/("(?:token|password|passwd|authorization|cookie|secret|credential|api[-_]?key)"\s*:\s*")[^"]*(")/gi, '$1[REDACTED]$2')
}

export function redactPageSpyValue(value, key = '', currentToken = '', seen = new WeakMap()) {
  if (SENSITIVE_KEY.test(key)) return '[REDACTED]'
  if (typeof value === 'string') {
    const withToken = currentToken ? value.split(String(currentToken)).join('[REDACTED]') : value
    // Storage libraries often serialize an envelope around the actual value.
    if (/^\s*[\[{]/.test(withToken)) {
      try { return JSON.stringify(redactPageSpyValue(JSON.parse(withToken), '', currentToken, seen)) } catch (_) {}
    }
    return redactString(withToken)
  }
  if (!value || typeof value !== 'object') return value
  if (seen.has(value)) return '[Circular]'
  seen.set(value, true)
  if (Array.isArray(value)) {
    const result = value.map((item) => redactPageSpyValue(item, key, currentToken, seen))
    if (value.length >= 2 && typeof value[0] === 'string' && SENSITIVE_KEY.test(value[0])) {
      result[1] = '[REDACTED]'
    }
    return result
  }
  const result = {}
  Object.keys(value).forEach((childKey) => {
    result[childKey] = redactPageSpyValue(value[childKey], childKey, currentToken, seen)
  })
  return result
}

export function createPageSpyDataProcessors({currentToken = '', shouldCapture = () => true} = {}) {
  const process = (item) => {
    if (!shouldCapture()) return false
    const sanitized = redactPageSpyValue(item, '', currentToken)
    if (Array.isArray(item)) item.splice(0, item.length, ...sanitized)
    else if (item && typeof item === 'object' && sanitized && sanitized !== item) {
      Object.keys(item).forEach((key) => delete item[key])
      Object.assign(item, sanitized)
    }
    return true
  }
  const network = (item) => {
    const urls = [item?.url, item?.requestUrl, item?.responseURL, item?.request?.url]
    if (urls.some((url) => String(url || '').includes('pagespy.vmos.cn'))) return false
    return process(item)
  }
  const storage = (item) => {
    if (!process(item)) return false
    const records = Array.isArray(item.data) ? item.data : [item]
    records.forEach((record) => {
      const name = record?.name ?? record?.key
      if (typeof name === 'string' && (SENSITIVE_KEY.test(name) || /(?:session[-_]?id|jsessionid|^sid$|page-spy-room)/i.test(name))) {
        if (Object.prototype.hasOwnProperty.call(record, 'value')) record.value = '[REDACTED]'
      }
    })
    return true
  }
  return {
    console: process,
    network,
    storage,
    database: storage,
  }
}

export function normalizePageSpyPolicy(response) {
  const data = response?.data
  if (!response?.success || !data || typeof data.enabled !== 'boolean') return null
  return {
    enabled: data.enabled,
    expiresAt: Number(data.expiresAt) || 0,
    leaseSeconds: Math.min(DEFAULT_LEASE_SECONDS, Math.max(1, Number(data.leaseSeconds) || DEFAULT_LEASE_SECONDS)),
    revision: String(data.revision || ''),
    pollIntervalSeconds: data.enabled ? 10 : 30,
  }
}

function pageSpyConfig(uid, environment, currentToken, shouldCapture, project, client) {
  return {
    api: 'pagespy.vmos.cn',
    project,
    title: `${client} | ${uid} | ${environment}`,
    useSecret: true,
    autoRender: false,
    disabledPlugins: ['PagePlugin', 'SystemPlugin'],
    dataProcessor: createPageSpyDataProcessors({currentToken, shouldCapture}),
  }
}

function loadPageSpySdk({
  windowRef,
  documentRef,
  sdkUrl = SDK_URL,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
  timeoutMs = 15_000,
}) {
  if (windowRef.PageSpy) return Promise.resolve(windowRef.PageSpy)
  return new Promise((resolve, reject) => {
    let settled = false
    let timeout = null
    const existing = documentRef.querySelector(`script[src="${sdkUrl}"]`)
    const finish = (error) => {
      if (settled) return
      settled = true
      if (timeout !== null) clearTimeoutImpl(timeout)
      if (existing?.removeEventListener) {
        existing.removeEventListener('load', onLoad)
        existing.removeEventListener('error', onError)
      }
      if (error && existing?.parentNode?.removeChild) existing.parentNode.removeChild(existing)
      if (script) {
        script.onload = null
        script.onerror = null
        if (error && script.parentNode?.removeChild) script.parentNode.removeChild(script)
      }
      if (error) reject(error)
      else if (windowRef.PageSpy) resolve(windowRef.PageSpy)
      else {
        (script || existing)?.remove?.()
        reject(new Error('PageSpy SDK missing'))
      }
    }
    const onLoad = () => finish(null)
    const onError = () => finish(new Error('PageSpy SDK load failed'))
    let script = null
    if (existing) {
      existing.addEventListener('load', onLoad, {once: true})
      existing.addEventListener('error', onError, {once: true})
      timeout = setTimeoutImpl(() => onError(new Error('PageSpy SDK load timeout')), timeoutMs)
      return
    }
    script = documentRef.createElement('script')
    script.src = sdkUrl
    script.async = true
    script.crossOrigin = 'anonymous'
    script.onload = onLoad
    script.onerror = onError
    timeout = setTimeoutImpl(() => onError(new Error('PageSpy SDK load timeout')), timeoutMs)
    documentRef.head.appendChild(script)
  })
}

export function createPageSpyController({
  store,
  fetchImpl = globalThis.fetch,
  windowRef = globalThis.window,
  documentRef = globalThis.document,
  sessionStorageRef,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
  now = () => Date.now(),
  environment = resolvePageSpyEnvironment(),
  project = 'vmos',
  client = 'proxy',
  policyTimeoutMs = POLICY_TIMEOUT_MS,
} = {}) {
  let generation = 0
  let timer = null
  let leaseTimer = null
  let policyAbort = null
  let sdk = null
  let sdkLoad = null
  let paused = false
  let deadline = 0
  let identitySeen = false
  let lastUserId = null
  const readRoomSession = () => {
    try { return (sessionStorageRef || windowRef?.sessionStorage)?.getItem(ROOM_SESSION_KEY) } catch (_) { return null }
  }
  const prepareRoomSession = (uid) => {
    try {
      const cached = JSON.parse(readRoomSession() || 'null')
    if (cached?.project === project && cached.title === `${client} | ${uid} | ${environment}` && cached.useSecret === true && cached.address && cached.secret) return
    } catch (_) {}
    clearRoomSession()
  }
  const clearRoomSession = () => {
    try { (sessionStorageRef || windowRef?.sessionStorage)?.removeItem(ROOM_SESSION_KEY) } catch (_) {}
  }

  const clearTimer = () => {
    if (timer !== null) clearTimeoutImpl(timer)
    timer = null
  }

  const clearLeaseTimer = () => {
    if (leaseTimer !== null) clearTimeoutImpl(leaseTimer)
    leaseTimer = null
  }

  const stopSdk = ({preserveRoom = true} = {}) => {
    const cached = preserveRoom ? readRoomSession() : null
    try { sdk?.abort?.() } catch (_) {}
    deadline = 0
    sdk = null
    sdkLoad = null
    if (cached) {
      try { (sessionStorageRef || windowRef?.sessionStorage)?.setItem(ROOM_SESSION_KEY, cached) } catch (_) {}
    } else if (!preserveRoom) clearRoomSession()
  }

  const schedule = (delay, uid, token, currentGeneration) => {
    clearTimer()
    timer = setTimeoutImpl(() => poll(uid, token, currentGeneration), Math.max(0, delay))
  }

  const requestPolicy = async (uid, token, currentGeneration) => {
    policyAbort?.abort()
    const controller = new AbortController()
    policyAbort = controller
    const timeout = setTimeoutImpl(() => controller.abort(), policyTimeoutMs)
    try {
      const response = await fetchImpl(POLICY_URL, {
        method: 'POST',
        credentials: 'omit',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({product: project, client, environment, userId: String(uid), token: String(token)}),
        signal: controller.signal,
      })
      if (response?.ok === false) throw new Error(`policy HTTP ${response.status || 0}`)
      if (currentGeneration !== generation || paused) return null
      return normalizePageSpyPolicy(await response.json())
    } finally {
      clearTimeoutImpl(timeout)
    }
  }

  const armLease = (policy, uid, token, currentGeneration) => {
    clearLeaseTimer()
    const currentTime = now()
    if (policy.expiresAt <= currentTime) return false
    const leaseDeadline = currentTime + policy.leaseSeconds * 1000
    deadline = Math.min(policy.expiresAt, leaseDeadline)
    leaseTimer = setTimeoutImpl(() => {
      if (currentGeneration !== generation) return
      generation += 1
      const nextGeneration = generation
      clearLeaseTimer()
      stopSdk()
      schedule(OFF_INTERVAL_MS, uid, token, nextGeneration)
    }, Math.max(0, deadline - currentTime))
    return true
  }

  const startSdk = async (uid, token, currentGeneration) => {
    if (sdk || sdkLoad) return
    sdkLoad = loadPageSpySdk({windowRef, documentRef, setTimeoutImpl, clearTimeoutImpl})
      .then((PageSpy) => {
        if (currentGeneration !== generation || paused || now() >= deadline) return
        prepareRoomSession(uid)
        sdk = new PageSpy(pageSpyConfig(uid, environment, token,
          () => currentGeneration === generation && !paused && now() < deadline, project, client))
      })
      .finally(() => {
        if (currentGeneration === generation) sdkLoad = null
      })
    await sdkLoad
  }

  const poll = async (uid, token, currentGeneration) => {
    if (currentGeneration !== generation) return
    try {
      const policy = await requestPolicy(uid, token, currentGeneration)
      if (currentGeneration !== generation) return
      if (!policy?.enabled) {
        clearLeaseTimer()
        stopSdk()
        schedule(OFF_INTERVAL_MS, uid, token, currentGeneration)
        return
      }
      if (!armLease(policy, uid, token, currentGeneration)) {
        stopSdk()
        schedule(OFF_INTERVAL_MS, uid, token, currentGeneration)
        return
      }
      await startSdk(uid, token, currentGeneration)
      if (currentGeneration !== generation || paused) return
      schedule(policy.pollIntervalSeconds * 1000, uid, token, currentGeneration)
    } catch (error) {
      if (currentGeneration !== generation) return
      clearLeaseTimer()
      stopSdk()
      schedule(OFF_INTERVAL_MS, uid, token, currentGeneration)
    }
  }

  const onIdentity = ([uid, token]) => {
    const authenticated = Boolean(uid && token)
    const preserveRoom = !identitySeen || (authenticated && String(uid) === lastUserId)
    if (authenticated) identitySeen = true
    lastUserId = authenticated ? String(uid) : null
    generation += 1
    const currentGeneration = generation
    clearTimer()
    clearLeaseTimer()
    policyAbort?.abort()
    policyAbort = null
    stopSdk({preserveRoom})
    if (!paused && uid && token) poll(uid, token, currentGeneration)
  }

  const pause = () => {
    if (paused) return
    paused = true
    generation += 1
    clearTimer()
    clearLeaseTimer()
    policyAbort?.abort()
    policyAbort = null
    stopSdk()
  }

  const resume = () => {
    if (!paused) return
    paused = false
    generation += 1
    clearTimer()
    clearLeaseTimer()
    policyAbort?.abort()
    policyAbort = null
    stopSdk()
    const uid = store.getters.userId
    const token = store.getters.token
    if (uid && token) poll(uid, token, generation)
  }

  const stop = store.watch(
    () => [store.getters.userId, store.getters.token],
    onIdentity,
    {immediate: true},
  )
  const onVisibilityChange = () => {
    // Switching to the PageSpy tab must not disconnect the page being debugged.
    // Keep polling and the hard deadline; pagehide still handles actual navigation.
    if (documentRef?.visibilityState !== 'hidden') resume()
  }
  const onPageHide = () => pause()
  const onPageShow = () => resume()
  documentRef?.addEventListener?.('visibilitychange', onVisibilityChange)
  windowRef?.addEventListener?.('pagehide', onPageHide)
  windowRef?.addEventListener?.('pageshow', onPageShow)
  return () => {
    generation += 1
    paused = true
    clearTimer()
    clearLeaseTimer()
    policyAbort?.abort()
    policyAbort = null
    stopSdk()
    documentRef?.removeEventListener?.('visibilitychange', onVisibilityChange)
    windowRef?.removeEventListener?.('pagehide', onPageHide)
    windowRef?.removeEventListener?.('pageshow', onPageShow)
    if (typeof stop === 'function') stop()
  }
}
